// components/CookieConsentBanner.tsx
'use client'

import { useState, useEffect, useTransition } from 'react'
import { setCookieConsent, type CookieConsent } from '@/app/actions/cookies'
import Link from 'next/link'
import styles from './CookieConsentBanner.module.css'

interface CookieConsentBannerProps {
  initialConsent: CookieConsent | null
}

export default function CookieConsentBanner({ initialConsent }: CookieConsentBannerProps) {
  const [showBanner, setShowBanner] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [isPending, startTransition] = useTransition()
  
  const [consent, setConsent] = useState<CookieConsent>({
    essential: true, // Immer aktiviert
    analytics: false,
    marketing: false
  })

  useEffect(() => {
    // Banner nur zeigen, wenn noch kein Consent vorhanden
    if (!initialConsent) {
      setShowBanner(true)
    }
  }, [initialConsent])

  const handleAcceptAll = () => {
    const fullConsent: CookieConsent = {
      essential: true,
      analytics: true,
      marketing: true
    }
    
    startTransition(async () => {
      await setCookieConsent(fullConsent)
      setShowBanner(false)
      
      // Analytics aktivieren
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          'analytics_storage': 'granted',
          'ad_storage': 'granted'
        })
      }
      
      // Seite neu laden um Analytics zu initialisieren
      window.location.reload()
    })
  }

  const handleRejectAll = () => {
    const minimalConsent: CookieConsent = {
      essential: true,
      analytics: false,
      marketing: false
    }
    
    startTransition(async () => {
      await setCookieConsent(minimalConsent)
      setShowBanner(false)
    })
  }

  const handleSavePreferences = () => {
    startTransition(async () => {
      await setCookieConsent(consent)
      setShowBanner(false)
      
      // Analytics basierend auf Auswahl aktivieren/deaktivieren
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          'analytics_storage': consent.analytics ? 'granted' : 'denied',
          'ad_storage': consent.marketing ? 'granted' : 'denied'
        })
      }
      
      // Seite neu laden wenn Analytics aktiviert wurde
      if (consent.analytics && !initialConsent?.analytics) {
        window.location.reload()
      }
    })
  }

  if (!showBanner) return null

  return (
    <>
      {/* Overlay */}
      <div className={styles.overlay} aria-hidden="true" />
      
      {/* Cookie Banner */}
      <div className={styles.banner} role="dialog" aria-modal="true" aria-labelledby="cookie-banner-title">
        <div className={styles.content}>
          {/* Header */}
          <div className={styles.header}>
            <h2 id="cookie-banner-title" className={styles.title}>
              🍪 Cookie-Einstellungen
            </h2>
          </div>

          {/* Main Content */}
          <div className={styles.body}>
            <p className={styles.description}>
              Wir verwenden Cookies, um Ihre Erfahrung auf unserer Website zu verbessern. 
              Einige Cookies sind für den Betrieb der Website erforderlich, während andere uns helfen, 
              die Website und Ihre Bedürfnisse besser zu verstehen.
            </p>

            {/* Cookie Details */}
            {showDetails && (
              <div className={styles.details}>
                {/* Essential Cookies */}
                <div className={styles.cookieCategory}>
                  <label className={styles.categoryLabel}>
                    <input
                      type="checkbox"
                      checked={consent.essential}
                      disabled
                      className={styles.checkbox}
                    />
                    <div className={styles.categoryInfo}>
                      <span className={styles.categoryTitle}>
                        Notwendige Cookies
                        <span className={styles.required}>(Erforderlich)</span>
                      </span>
                      <span className={styles.categoryDescription}>
                        Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                      </span>
                    </div>
                  </label>
                </div>

                {/* Analytics Cookies */}
                <div className={styles.cookieCategory}>
                  <label className={styles.categoryLabel}>
                    <input
                      type="checkbox"
                      checked={consent.analytics}
                      onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
                      className={styles.checkbox}
                    />
                    <div className={styles.categoryInfo}>
                      <span className={styles.categoryTitle}>Analyse-Cookies</span>
                      <span className={styles.categoryDescription}>
                        Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, 
                        indem sie Informationen anonym sammeln und melden.
                      </span>
                    </div>
                  </label>
                </div>

                {/* Marketing Cookies */}
                <div className={styles.cookieCategory}>
                  <label className={styles.categoryLabel}>
                    <input
                      type="checkbox"
                      checked={consent.marketing}
                      onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                      className={styles.checkbox}
                    />
                    <div className={styles.categoryInfo}>
                      <span className={styles.categoryTitle}>Marketing-Cookies</span>
                      <span className={styles.categoryDescription}>
                        Diese Cookies werden verwendet, um Werbung zu liefern, die für Sie relevanter ist. 
                        Sie können auch die Anzahl der Anzeigen begrenzen und die Effektivität von Werbekampagnen messen.
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Toggle Details Button */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className={styles.toggleButton}
              type="button"
            >
              {showDetails ? 'Weniger anzeigen' : 'Mehr Informationen'}
            </button>
          </div>

          {/* Footer with Actions */}
          <div className={styles.footer}>
            <div className={styles.links}>
              <Link href="/datenschutz" className={styles.link}>
                Datenschutzerklärung
              </Link>
              <span className={styles.separator}>•</span>
              <Link href="/impressum" className={styles.link}>
                Impressum
              </Link>
            </div>
            
            <div className={styles.actions}>
              <button
                onClick={handleRejectAll}
                className={styles.rejectButton}
                disabled={isPending}
                type="button"
              >
                Nur Notwendige
              </button>
              
              {showDetails && (
                <button
                  onClick={handleSavePreferences}
                  className={styles.saveButton}
                  disabled={isPending}
                  type="button"
                >
                  Auswahl speichern
                </button>
              )}
              
              <button
                onClick={handleAcceptAll}
                className={styles.acceptButton}
                disabled={isPending}
                type="button"
              >
                Alle akzeptieren
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
